import { Smartphone, Cpu, Clock3, CircuitBoard, Radio, Gauge, CalendarCheck, CalendarClock, Wine, Activity, Flag, Mountain, Thermometer, Wind, CircleGauge, Database } from 'lucide-react';
import { parseDeviceInfoResponse } from '../../services/devicecommands';
import { formatDiagnosticParameter } from '../../utils/formatDiagnosticParameter';

const CALIBRATION_GROUPS = [
  {
    title: 'Device Overview',
    icon: Smartphone,
    items: [
      { label: 'Device Id', icon: Cpu, paths: ['cbid'], inline: true },
      { label: 'HandSet', icon: Smartphone, paths: ['devid'], inline: true },
      { label: 'Device Time', icon: Clock3, paths: ['updatedtime'] }
    ]
  },
  {
    title: 'Firmware Data',
    icon: CircuitBoard,
    items: [
      { label: 'S Firmware', icon: Cpu, paths: ['fwversion'] },
      { label: 'CB Firmware', icon: CircuitBoard, paths: ['cbversion'] },
      { label: 'S3 Version', icon: Radio, paths: ['s3version.String', 's3version'] }
    ]
  },
  {
    title: 'Calibration Data',
    icon: Gauge,
    items: [
      { label: 'Present Calibration Time', icon: CalendarCheck, paths: ['caldate'] },
      { label: 'Next Calibration Time', icon: CalendarClock, paths: ['nextcaldate.String', 'nextcaldate'] },
      { label: 'Alcohol Offset', icon: Wine, paths: ['alcooffset.String', 'alcooffset'] },
      { label: 'BrAC Peak Factor', icon: Activity, paths: ['bracpeakfactor'] },
      { label: 'Offset Pressure', icon: Gauge, paths: ['offset_pressure'] },
      { label: 'Engine Run ADC', icon: Activity, paths: ['erunadc.String', 'erunadc'] },
      { label: 'Cal Flag', icon: Flag, paths: ['calflag.String', 'calflag'] },
      { label: 'Altitude', icon: Mountain, paths: ['altitude.String', 'altitude'] },
      { label: 'Device Temp', icon: Thermometer, paths: ['ambtemp.String', 'ambtemp'] },
      { label: 'Breath Temp', icon: Wind, paths: ['breathetemp.String', 'breathetemp'] }
    ]
  }
];

const A200U_DEVICE_INFO_GROUPS = [
  {
    title: 'Device & Firmware',
    icon: Smartphone,
    parameters: [
  'mac_buff',
  'fversion',
  'cam1_time',
  'datebuf',
  'local_params.next_cal_date',
  'cb_version',
  'eng_battery_adc',
  'cb_mac_neo'
]
  },
  {
    title: 'Sensor & Calibration',
    icon: Gauge,
    parameters: [
      'offset_pre',
      'alco_sen_offset',
      'tmp75',
      'bt',
      'a',
      'Brac_peak_factor30',
      'calibration_set_flag'
    ]
  },
  {
    title: 'Device Configuration',
    icon: Cpu,
    parameters: [
  'proxy_en_dis',
  'brao_en_dis',
  'temp_en_dis',
  'fail_count_config',
  'refused_count_config',
  'eng_off_min_config',
  'batter_tamp_time',
  'insufficient_count_config',
  'first_rr_timer_config',
  'lc_days_config',
  'cal_grace_config',
  'second_rr_timer_config',
  'initial_lock_timer_config',
  'first_initial_lockout_timer',
  'second_initial_lockout_timer',
  'display_results_flag',
  'rr_test_fail_refuse_timer',
  'lock_days',
  'brac_sol_cal_value',
  'ab_current_params.init_brac_threshold',
  'custom_vol',
  'custom_time_blow',
  'ab_current_params.breath_temp_min_threshold',
  'ab_current_params.breath_temp_max_threshold',
  'random_test',
  'device_status_flag',
  'device_type_flag',
  'device_type_buff'
]
    // parameters: [
    //   'proxy_en_dis',
    //   'brao_en_dis',
    //   'temp_en_dis',
    //   'fail_count_config',
    //   'refused_count_config',
    //   'eng_off_min_config',
    //   'batter_tamp_time',
    //   'insufficient_count_config',
    //   'first_rr_timer_config',
    //   'lc_days_config',
    //   'cal_grace_config',
    //   'second_rr_timer_config',
    //   'initial_lock_timer_config',
    //   'first_initial_lockout_timer',
    //   'second_initial_lockout_timer',
    //   'display_results_flag',
    //   'rr_test_fail_refuse_timer',
    //   'calibration_solution_val',
    //   'fail_pass_thershold',
    //   'custom_vol',
    //   'custom_time_blow',
    //   'bt_min_threshold',
    //   'bt_max_threshold',
    //   'device_status_flag',
    //   'device_type_flag',
    //   'DEVICE_TYPE'
    // ]
  }
];
const formatDeviceInfoLabel = (parameter) => {
  if (parameter === 'mac_buff') return 'Sampler Id';
  if (parameter === 'fversion') return 'Firmware Version';
  if (parameter === 'cam1_time') return 'Device Time';
  if (parameter === 'datebuf') return 'Calibration Date';
  if (parameter === 'timebuf') return 'Time';
  if (parameter === 'local_params.next_cal_date') return 'Next Calibration Date';

  if (parameter === 'cb_version') return 'CB Version';
  if (parameter === 'eng_battery_adc') return 'Engine Battery ADC';
  if (parameter === 'cb_mac_neo') return 'ControlBoard Id';

  if (parameter === 'offset_pre') return 'Offset Pressure';
  if (parameter === 'alco_sen_offset') return 'Alcohol Sensor Offset';
  if (parameter === 'tmp75') return 'Device Temperature';
  if (parameter === 'bt') return 'Breathe Temperature';
  if (parameter === 'a') return 'Altitude';
  if (parameter === 'Brac_peak_factor30') return 'BrAC Peak Factor';
  if (parameter === 'calibration_set_flag') return 'Calibration Set Flag';

  if (parameter === 'proxy_en_dis') return 'Co2 Enable / Disable';
  if (parameter === 'brao_en_dis') return 'BrAO Enable / Disable';
  if (parameter === 'temp_en_dis') return 'Temperature Enable / Disable';

  if (parameter === 'fail_count_config') return 'Fail Count Config';
  if (parameter === 'refused_count_config') return 'Refused Count Config';
  if (parameter === 'eng_off_min_config') return 'Engine Off Min Config';
  if (parameter === 'batter_tamp_time') return 'Battery Tamper Time';
  if (parameter === 'insufficient_count_config') return 'Insufficient Count Config';
  if (parameter === 'first_rr_timer_config') return 'First RR Timer Config';
  if (parameter === 'lc_days_config') return 'LC Days Config';
  if (parameter === 'cal_grace_config') return 'Calibration Grace Period';
  if (parameter === 'second_rr_timer_config') return 'Second RR Timer Config';
  if (parameter === 'initial_lock_timer_config') return 'Initial Lock Timer Config';
  if (parameter === 'first_initial_lockout_timer') return 'First Initial Lockout Timer';
  if (parameter === 'second_initial_lockout_timer') return 'Second Initial Lockout Timer';
  if (parameter === 'display_results_flag') return 'Display Results Flag';
  if (parameter === 'rr_test_fail_refuse_timer') return 'RR Test Fail Refuse Timer';

  if (parameter === 'lock_days') return 'Lock Days';
  if (parameter === 'brac_sol_cal_value') return 'BrAC Solution Calibration Value';
  if (parameter === 'ab_current_params.init_brac_threshold') return 'Initial BrAC Threshold';

  if (parameter === 'custom_vol') return 'Custom Blow Volume';
  if (parameter === 'custom_time_blow') return 'Custom Blow Time';

  if (parameter === 'ab_current_params.breath_temp_min_threshold') {
    return 'Breath Temperature Min Threshold';
  }

  if (parameter === 'ab_current_params.breath_temp_max_threshold') {
    return 'Breath Temperature Max Threshold';
  }

  if (parameter === 'random_test') return 'Random Test';

  if (parameter === 'device_status_flag') return 'Device Status Flag';
  if (parameter === 'device_type_flag') return 'Device Type Flag';
  if (parameter === 'device_type_buff') return 'Device Type';

  return parameter
    .replace(/_/g, ' ')
    .replace(/\b\w/g, char => char.toUpperCase());
};

// Command 80 does not return the same fields for every device model.  Keep the
// presentation driven by the fields received instead of maintaining a static
// layout per model (for example, A200U can return fewer fields than A200U).
const getDeviceInfoPresentation = (parameter) => {
  const name = String(parameter).toLowerCase();

  if (/(deviceid|controlboard|cbid|cb_mac|mac_buff|(^|_)(device|control|board|cb|mac|id)(_|$))/.test(name)) {
    return { group: 'Device Overview', groupIcon: Smartphone, icon: Cpu };
  }
  if (/(firmware|fversion|fwversion|cb_version|version)/.test(name)) {
    return { group: 'Firmware', groupIcon: CircuitBoard, icon: CircuitBoard };
  }
  if (/(date|time|calibration|caldate|updated)/.test(name)) {
    return { group: 'Dates & Service', groupIcon: CalendarCheck, icon: CalendarClock };
  }
  if (/(temp|pressure|offset|alco|brac|bac|adc|altitude|blow|sensor)/.test(name)) {
    return {
      group: 'Sensor & Calibration',
      groupIcon: Gauge,
      icon: /temp/.test(name) ? Thermometer : Gauge
    };
  }
  if (/(battery|engine|lock|timer|count|flag|proxy|display|enable|disable|config|status)/.test(name)) {
    return { group: 'Configuration', groupIcon: Cpu, icon: CircleGauge };
  }

  return { group: 'Additional Device Parameters', groupIcon: Database, icon: Database };
};


const pathValue = (record, path) => path.split('.').reduce((value, key) => value?.[key], record);
const displayValue = value => {
  if (value && typeof value === 'object' && 'String' in value) return String(value.String || '').trim();
  return value == null ? '' : String(value).trim();
};

export const getCalibrationValue = (record, paths) =>
  paths.map(path => displayValue(pathValue(record, path))).find(value => value !== '') || 'N/A';

export const getA200uCalibrationParameters = record => {
    const raw = record?.fullresp;
    const response = typeof raw === 'string' ? raw : raw?.Valid === false ? '' : raw?.String ?? raw?.string ?? '';
    return record?.a200uParameters ?? parseDeviceInfoResponse(String(response).trim().replace(/^NA:/i, ''), { devicemode: 'A200U' });
};

export const buildCalibrationGroups = (record, isA200u) => {
  if (isA200u) {
    const parameters = getA200uCalibrationParameters(record);
    return A200U_DEVICE_INFO_GROUPS.map(group => ({
      ...group,
      items: group.parameters.map(parameter => ({
        label: formatDeviceInfoLabel(parameter),
        icon: getDeviceInfoPresentation(parameter).icon,
        value: formatDiagnosticParameter(parameter, parameters[parameter])
      }))
    }));
  }
  return CALIBRATION_GROUPS.map(group => ({
    ...group,
    items: group.items.map(item => ({
      ...item,
      value: getCalibrationValue(record, item.paths)
    }))
  }));

};

export { A200U_DEVICE_INFO_GROUPS, formatDeviceInfoLabel, getDeviceInfoPresentation };
